import _ from 'lodash';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import * as StarWebPrint from 'utils/printer/StarWebPrint';
import { NetworkPrinterJob, dashboard } from 'types';
import { TockNative } from 'middleware/TockNative';
import {
  createBase64Image,
  isWebPrinter,
  printTextToNative,
  submitJobsToNative,
  supportsImagePrinting,
} from 'utils/printer/networkPrinterUtils';
import {
  createWebPrinterPlainTextRequest,
  submitJobsToWebPrint,
  toWebPrintUri,
} from 'utils/printer/webPrinterUtils';
import { isInApp } from 'utils/app';
import AppConstants from 'constants/AppConstants';

export function fetchAll() {
  return {
    [TockNative]: {
      type: AppConstants.NETWORK_PRINTERS_FETCH_ALL,
    },
  };
}

export function printText(
  printer: dashboard.BusinessPrinter,
  text: string,
  showToast?: boolean,
  shouldTrack?: boolean
) {
  return submitJobs([{ printer, text }], showToast, shouldTrack);
}

export function printPlainText(printer: dashboard.BusinessPrinter, text: string) {
  return (dispatch: Dispatch) => {
    if (isWebPrinter(printer)) {
      const trader = new StarWebPrint.StarWebPrintTrader({
        url: toWebPrintUri(printer),
        papertype: 'normal',
      });
      trader.onError = () => {
        dispatch(submitJobsFailed('A chit failed to print') as Action);
      };
      trader.sendMessage({ request: createWebPrinterPlainTextRequest(text) });
    } else {
      printTextToNative({
        printer,
        text,
      });
    }
  };
}

export async function submitJobs(
  jobs: NetworkPrinterJob[],
  usePrintQueue = false,
  showToast = true,
  shouldTrack = true,
  onFailure = _.noop
) {
  return async (dispatch: Dispatch) => {
    const partitionedJobs = _.partition(jobs, (job) => isWebPrinter(job.printer));
    const webPrinterJobs = partitionedJobs[0];
    const nativePrinterJobs = partitionedJobs[1];

    if (!showToast) {
      dispatch({ type: AppConstants.NETWORK_PRINTERS_DO_NOT_SHOW_PROGRESS });
    }

    if (isInApp() && !_.isEmpty(nativePrinterJobs)) {
      dispatch({
        type: AppConstants.NETWORK_PRINTERS_SUBMIT,
        payload: nativePrinterJobs,
        context: { shouldTrack },
      });
      Promise.all(
        // Augments the request to the native printer based on whether we think
        // it supports image or just text printing.
        _.map(nativePrinterJobs, (job) =>
          supportsImagePrinting(job.printer)
            ? createBase64Image(job.text, job.printer).then(
                (base64Image) =>
                  ({
                    ...job,
                    base64Image,
                  }) as NetworkPrinterJob
              )
            : Promise.resolve({
                ...job,
                printPlainText: true,
              })
        )
      ).then((jobs) => submitJobsToNative(jobs, usePrintQueue));
    }

    if (!isInApp() && !_.isEmpty(webPrinterJobs)) {
      dispatch({
        type: AppConstants.NETWORK_PRINTERS_SUBMIT,
        payload: webPrinterJobs,
        context: { shouldTrack },
      });
      const onSuccess = () => dispatch(submitJobDone());
      const onError = (err: string) => {
        dispatch(submitJobsFailed(err));
        onFailure();
      };
      submitJobsToWebPrint(webPrinterJobs, onSuccess, onError);
      dispatch(submitJobsDone());
    }
  };
}

export function submitJobsDone() {
  return {
    type: AppConstants.NETWORK_PRINTERS_SUBMIT_DONE,
  };
}

export function submitJobsFailed(errorMessage: string): Action {
  return {
    type: AppConstants.NETWORK_PRINTERS_SUBMIT_FAILED,
    toastMessage: {
      type: 'error',
      message: errorMessage,
    },
  };
}

export function submitJobDone() {
  return {
    type: AppConstants.NETWORK_PRINTERS_JOB_DONE,
  };
}

export function jobFailed() {
  return {
    type: AppConstants.NETWORK_PRINTERS_JOB_FAILED,
  };
}

export function closePrinterProgress() {
  return {
    type: AppConstants.NETWORK_PRINTERS_DO_NOT_SHOW_PROGRESS,
  };
}

export function versions(nativeAppVersion: string, osVersion: string) {
  return {
    type: AppConstants.GET_NATIVE_VERSIONS_DONE,
    payload: {
      nativeAppVersion,
      osVersion,
    },
  };
}
